import React from 'react';

// Plain-language explanation of each scaling mode, kept faithful to the
// formulas in backend/services/scaling.js:calculateScaledLots(). Shared by the
// Follow/Edit modal (ScalingModal) and the Find Traders page so copiers can
// pick the right one. Single source of truth — edit the copy here only.
export const SCALING_MODE_INFO = [
  {
    value: 'ratio',
    label: 'Auto Ratio (equity-based)',
    formula: 'your lots = trader lots × (your equity ÷ trader equity) × risk multiplier',
    summary:
      "Scales the trader's lot size by how your account size compares to theirs, so you both take proportionally the same risk relative to your capital. This is the recommended default for accounts of different sizes.",
    example:
      'Trader opens 1.0 lot on a $10,000 account. With $5,000 you copy 0.50 lots; with $20,000 you copy 2.0 lots.',
    note: 'If your trader’s equity isn’t known yet, this temporarily falls back to copying their exact lot size.',
  },
  {
    value: 'mirror',
    label: 'Mirror (same lots as trader)',
    formula: 'your lots = trader lots × risk multiplier',
    summary:
      "Copies the trader's exact lot size, with no adjustment for account size.",
    example: 'Trader opens 1.0 lot → you open 1.0 lot (before your risk multiplier and lot limits).',
    note: 'Caution: a small account mirroring a large trader can be heavily over-leveraged. Use the risk multiplier and max lot size to keep this in check.',
  },
  {
    value: 'fixed_lot',
    label: 'Fixed Lot Size',
    formula: 'your lots = fixed lot size × risk multiplier',
    summary:
      "Every copied trade uses the same lot size you set, regardless of what the trader traded or how big either account is. The trader's lot size is ignored entirely.",
    example: 'Set 0.10 → every copied trade is 0.10 lots (before your risk multiplier and lot limits).',
    note: 'Best when you want predictable, uniform exposure on every trade.',
  },
  {
    value: 'equity_percent',
    label: 'Equity Percent',
    formula: 'your lots = (trader lots ÷ trader equity) × your equity × risk multiplier',
    summary:
      "Replicates the trader's risk expressed as lots per unit of equity, then applies it to your equity. In practice this gives the same result as Auto Ratio — pick whichever way of thinking about it you prefer.",
    example:
      'Trader risks 1.0 lot on $10,000 (0.0001 lots per $1). On a $5,000 account that becomes 0.50 lots.',
    note: 'Same equity fallback as Auto Ratio if the trader’s equity isn’t known yet.',
  },
];

// All four modes round to your broker's lot step (usually 0.01) and are then
// held to your Min Lot Size, and to your Max Lot Size WHEN ONE IS SET (it is
// 0 = disabled by default, so there is no upper clamp unless the copier adds
// one).
export const SCALING_COMMON_NOTE =
  'In every mode the result is rounded to your broker’s lot step (usually 0.01), raised to your Min Lot Size if it comes out below it, and capped at your Max Lot Size if you have set one (0 means no cap). Because of the Min Lot Size floor, very small calculated sizes are raised to your minimum.';

export default function ScalingModesInfoModal({ onClose }) {
  return (
    <div
      className="modal show d-block"
      tabIndex="-1"
      style={{ background: 'rgba(0,0,0,0.7)', overflowY: 'auto', zIndex: 1060 }}
      onClick={onClose}
    >
      <div className="modal-dialog modal-lg" style={{ marginTop: 16, marginBottom: 16 }}
        onClick={e => e.stopPropagation()}>
        <div className="modal-content" style={{ background: '#27293d', color: '#fff' }}>
          <div className="modal-header border-secondary">
            <h5 className="modal-title">How scaling modes work</h5>
            <button className="close text-white" onClick={onClose}>&times;</button>
          </div>
          <div className="modal-body">
            <p className="text-muted">
              Each mode decides how big your copied trade is relative to the
              trader's. Your <strong>risk multiplier</strong> (0.1×–3×) is then
              applied on top in every mode.
            </p>
            {SCALING_MODE_INFO.map(m => (
              <div key={m.value} style={{ marginBottom: 18 }}>
                <h6 className="mb-1" style={{ color: '#1d8cf8' }}>{m.label}</h6>
                <div className="small text-monospace mb-2"
                  style={{ background: '#1e1e2f', padding: '6px 10px', borderRadius: 4 }}>
                  {m.formula}
                </div>
                <p className="mb-1">{m.summary}</p>
                <p className="mb-1 small"><strong>Example:</strong> {m.example}</p>
                {m.note && <p className="mb-0 small text-muted">{m.note}</p>}
              </div>
            ))}
            <hr className="border-secondary" />
            <p className="small text-muted mb-0">{SCALING_COMMON_NOTE}</p>
          </div>
          <div className="modal-footer border-secondary">
            <button type="button" className="btn btn-primary" onClick={onClose}>
              Got it
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
